import { Stack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';

import { StyledScrollWrapper } from '$/components/common/StyledScrollWrapper';
import { useAuthorization } from '$/components/core/Authentication/hooks/useAuthorization';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { BackToTopButton } from '$/pages/DashboardPages/components/BackToTopButton';
import { ProjectFolderView } from '$/pages/DashboardPages/pages/Projects/components/ProjectFolderView';
import { ProjectModal } from '$/pages/DashboardPages/pages/Projects/components/ProjectModal';
import { ProjectOverview } from '$/pages/DashboardPages/pages/Projects/components/ProjectOverview';
import { ProjectsRoute } from '$/routes/ProjectsRoute';
import { type ProjectFolder } from '$/services/usecases/projects/mapper/editorProject';
import { projectFoldersQuery } from '$/services/usecases/projects/queries';
import { useLastSeenStore } from '$/stores/useLastSeenStore';
import { useProjectStore } from '$/stores/useProjectStore';

export const ProjectPage = () => {
  const ref = useRef<HTMLDivElement>(null);
  const projects = useProjectStore.useProjects();
  const { hasPermission } = useAuthorization();
  const user = useAuthenticationStore.useUser();
  const { data } = useQuery(projectFoldersQuery());

  const { folderId } = ProjectsRoute.useSearch();
  const lastSeenFolders = useLastSeenStore.useLastSeenProjectFolders();

  const sortFolders = (items: ProjectFolder[]) => {
    return items.sort((a, b) => {
      const aLastSeenIndex = lastSeenFolders.findIndex(
        (folder) => folder.id === a.id,
      );
      const bLastSeenIndex = lastSeenFolders.findIndex(
        (folder) => folder.id === b.id,
      );

      if (aLastSeenIndex === -1 && bLastSeenIndex === -1) return 0;
      if (aLastSeenIndex === -1) return 1;
      if (bLastSeenIndex === -1) return -1;
      return bLastSeenIndex - aLastSeenIndex;
    });
  };

  const folders: ProjectFolder[] = data?.response.payload
    ? sortFolders([...data.response.payload])
    : [];

  const shownProjects = projects.filter((project) => {
    if (folderId === null && project.schoolClassId == null) {
      return true;
    }

    if (
      folderId === '_schoolclass' &&
      (project.schoolClassId != null ||
        project.schoolClassIds?.includes(user?.classId ?? '')) &&
      hasPermission('Project_SchoolClassFolder')
    )
      return true;

    return project.parentId === folderId && project.schoolClassId == null;
  });

  const getSelectedFolder = () =>
    folders.find((folder) => folder.id === folderId) ?? null;

  const selectedFolder: ProjectFolder | null = getSelectedFolder();

  return (
    <Stack ref={ref} layerStyle='DashboardSection' w='full'>
      <StyledScrollWrapper>
        {selectedFolder != null ? (
          <ProjectFolderView folder={selectedFolder} projects={shownProjects} />
        ) : (
          <ProjectOverview
            projects={shownProjects}
            unfilteredProjects={projects}
            folders={folders}
          />
        )}

        <ProjectModal />

        <BackToTopButton containerRef={ref} right='65px' bottom='65px' />
      </StyledScrollWrapper>
    </Stack>
  );
};
